import React, { Fragment } from "react"
import {
  Checkbox, CheckboxGroup, Flex, Text,
} from "@chakra-ui/react"
import { Form, FormType } from "sharedTypes"
import { getFormsByType } from "utilities/forms"
import FilterOptionLabel from "../FilterOptionLabel"

type Props = {
  forms: Form[]
  selectedFormIdFilters: string[]
  onSelectFormIdFilters: (filters) => void
}

const FormTypeFilter = ({ forms, selectedFormIdFilters, onSelectFormIdFilters }: Props) => {
  const formsByType = (type: FormType) => getFormsByType(forms, type).map((form: Form) => ({
    externalId: form.externalId,
    name: form.name,
  }))

  const filterOptions = Object.values(FormType).map((type) => ({
    title: <FilterOptionLabel type={type} />,
    value: type,
    type,
    subOptions: formsByType(type),
  })).filter((option) => option.subOptions.length > 0)

  const handleAddAllItemsInAGroup = (newItems: string[]) => {
    // Filter out duplicates from the new items
    const uniqueNewItems = newItems.filter((item) => !selectedFormIdFilters.includes(item))

    // If there are all of the items are selected, we remove them to clear the selection
    if (!uniqueNewItems.length) {
      onSelectFormIdFilters((prevItems) => prevItems.filter((item) => !newItems.includes(item)))
    }

    // Update the state with the merged list of existing items and unique new items
    onSelectFormIdFilters((prevItems) => [...prevItems, ...uniqueNewItems])
  }

  return (
    <>
      <Flex
        position="sticky"
        top={0}
        zIndex={1}
        h={12}
        justifyContent="center"
        alignItems="center"
        backgroundColor="gray.100"
        borderBottomWidth={1}
      >
        <Text fontWeight="semibold">Filter by Form Type</Text>
      </Flex>
      {filterOptions.map(({ title, value, subOptions }) => {
        const externalIds = subOptions.map(({ externalId }) => externalId)
        const allChecked = externalIds.every(
          (item) => selectedFormIdFilters.includes(item),
        )
        const isIndeterminate = externalIds.some(
          (item) => selectedFormIdFilters.includes(item),
        ) && !allChecked

        return (
          <Fragment key={value}>
            <Flex
              h={12}
              justifyContent="center"
              alignItems="center"
              borderBottomWidth={1}
              pl={4}
            >
              <Checkbox
                w="100%"
                height="100%"
                isChecked={allChecked}
                isIndeterminate={isIndeterminate}
                onChange={() => handleAddAllItemsInAGroup(externalIds)}
              >
                {title}
              </Checkbox>
            </Flex>
            <CheckboxGroup
              value={selectedFormIdFilters}
              onChange={onSelectFormIdFilters}
            >
              {subOptions.map(({ name: subOptionName, externalId }) => (
                <Flex
                  key={externalId}
                  pt={4}
                  pb={3}
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  borderBottomWidth={1}
                  pl={10}
                >
                  <Checkbox alignItems="flex-start" key={externalId} value={externalId}>
                    <Text mt={-1} noOfLines={1}>{subOptionName}</Text>
                  </Checkbox>
                </Flex>
              ))}
            </CheckboxGroup>
          </Fragment>
        )
      })}
    </>
  )
}

export default FormTypeFilter
